import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, AlertTriangle, Play, ChevronLeft, ChevronRight, Flag, BookOpen, CheckCircle, XCircle, Timer, Zap, Shield, Shuffle, BarChart3, LogOut, Volume2, VolumeX } from 'lucide-react';
import { useExam } from '@/lib/api-adapters';
import { useSimulationSession } from '@/hooks/useSimulationSession';
import { publicConfigApi } from '@/services/api';
import { trackQuizAbandon, trackSimulationStart } from '@/lib/track';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { QuestionCard } from '@/components/practice/QuestionCard';
import { OptionsList } from '@/components/practice/OptionsList';
import { ResultsSummary } from '@/components/practice/ResultsSummary';
import { useTimer } from '@/hooks/useTimer';
import { formatTime } from '@/lib/utils';
import { playTick, playUrgentTick, isSoundMuted, setSoundMuted } from '@/lib/sounds';

export function SimulationPage() {
  const navigate = useNavigate();
  const { examId } = useParams<{ examId: string }>();
  const { exam, loading: examLoading } = useExam(examId);
  useDocumentTitle(exam ? `${exam.name} — Simulation` : 'Simulation');

  const [configLoaded, setConfigLoaded] = useState(false);
  const [questionCount, setQuestionCount] = useState(50);
  const [durationMinutes, setDurationMinutes] = useState(60);

  useEffect(() => {
    publicConfigApi
      .get()
      .then(({ data }) => {
        if (data.fullExamQuestionsCount) setQuestionCount(data.fullExamQuestionsCount);
        if (data.fullExamDurationMinutes) setDurationMinutes(data.fullExamDurationMinutes);
      })
      .catch(() => {})
      .finally(() => setConfigLoaded(true));
  }, []);

  // Nombre de questions du format "complet" (examen blanc), borné par la banque.
  const fullCount = useMemo(() => {
    if (!exam) return questionCount;
    return Math.min(questionCount, exam.totalQuestions || questionCount, 100);
  }, [exam, questionCount]);

  // Formats proposés : « Éclair » (court, ~5 min) réduit l'abandon massif des
  // longues simulations ; « Complet » reste l'examen blanc. Bornés par la
  // banque de questions et dédupliqués pour les petits examens.
  const lengthOptions = useMemo(() => {
    const max = Math.min(exam?.totalQuestions || fullCount, 100);
    const raw = [
      { key: 'flash', label: 'Éclair', hint: 'Rapide', value: Math.min(15, max) },
      { key: 'medium', label: 'Moyen', hint: 'Intermédiaire', value: Math.min(40, max) },
      { key: 'full', label: 'Complet', hint: 'Examen blanc', value: Math.min(fullCount, max) },
    ];
    const seen = new Set<number>();
    return raw.filter((o) => o.value > 0 && !seen.has(o.value) && seen.add(o.value));
  }, [exam, fullCount]);

  const [chosenCount, setChosenCount] = useState<number | null>(null);
  const effectiveQuestionCount = chosenCount ?? lengthOptions[0]?.value ?? fullCount;

  // Temps imparti proportionnel au nombre de questions (min 5 min).
  const baseDurationMin = exam?.duration || durationMinutes;
  const effectiveDurationMinutes = Math.max(
    5,
    Math.round((baseDurationMin * effectiveQuestionCount) / (fullCount || effectiveQuestionCount)),
  );
  const durationSeconds = effectiveDurationMinutes * 60;

  const simulation = useSimulationSession(examId || '', effectiveQuestionCount);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showQuitModal, setShowQuitModal] = useState(false);

  // On mobile we auto-advance to the next question right after an answer is
  // picked (matches the immersive full-screen layout used below the lg breakpoint).
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const timerRunning = simulation.phase === 'running';

  const handleTimeUp = useCallback(() => {
    if (simulation.phase === 'running') {
      simulation.submitExam();
    }
  }, [simulation]);

  const handleStart = () => {
    trackSimulationStart(examId ?? '', exam?.name ?? '', effectiveQuestionCount);
    simulation.startExam();
  };

  const handleSubmit = () => {
    setShowReviewModal(true);
  };

  const handleQuit = () => {
    if (simulation.phase === 'running') {
      setShowQuitModal(true);
    } else {
      navigate(`/exam/${examId}`);
    }
  };

  const handleConfirmQuit = () => {
    // Quitter avant la fin = abandon : combien de questions répondues (drop-off).
    trackQuizAbandon(
      'simulation',
      examId ?? '',
      simulation.answeredCount,
      simulation.totalQuestions,
    );
    setShowQuitModal(false);
    // Abandon explicite : la session est enregistrée comme `abandoned` (pas
    // `completed`) → stats non polluées, aucune récompense.
    simulation.submitExam({ abandoned: true });
  };

  const advanceTimerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (advanceTimerRef.current) window.clearTimeout(advanceTimerRef.current);
  }, []);

  const handleAnswerSelect = useCallback(
    (index: number) => {
      simulation.selectAnswer(index);
      if (advanceTimerRef.current) window.clearTimeout(advanceTimerRef.current);
      // Mobile only: let the selection highlight land, then move to the next
      // question automatically (no-op on the last question / non-running phase).
      if (isMobile && simulation.currentIndex < simulation.totalQuestions - 1) {
        const nextIndex = simulation.currentIndex + 1;
        advanceTimerRef.current = window.setTimeout(() => {
          simulation.goToQuestion(nextIndex);
        }, 280);
      }
    },
    [isMobile, simulation],
  );

  // --- Scroll refs for question navigator ---
  const navScrollRef = useRef<HTMLDivElement>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const questionBtnRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  const setQuestionBtnRef = useCallback((index: number, el: HTMLButtonElement | null) => {
    if (el) questionBtnRefs.current.set(index, el);
    else questionBtnRefs.current.delete(index);
  }, []);

  useEffect(() => {
    const container = navScrollRef.current;
    const btn = questionBtnRefs.current.get(simulation.currentIndex);
    if (!container || !btn) return;
    const containerRect = container.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    const offset = btnRect.left - containerRect.left - containerRect.width / 2 + btnRect.width / 2;
    container.scrollBy({ left: offset, behavior: 'smooth' });
    // Reset the question body to the top so the new question starts from its heading.
    contentScrollRef.current?.scrollTo({ top: 0 });
  }, [simulation.currentIndex]);

  // --- Loading ---
  if (examLoading || !configLoaded || simulation.phase === 'loading') {
    return (
      <div className="flex items-center justify-center h-full bg-app">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-fg-subtle">Chargement de la simulation...</p>
        </div>
      </div>
    );
  }

  if (!exam || !examId) {
    return (
      <div className="flex items-center justify-center h-full bg-app">
        <div className="text-center px-4">
          <p className="text-base font-medium text-fg-subtle">Examen introuvable</p>
          <button onClick={() => navigate('/dashboard')} className="mt-4 px-5 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium">
            Retour
          </button>
        </div>
      </div>
    );
  }

  const gradient = exam.bgGradient || 'from-emerald-500 to-teal-600';

  // --- Completed ---
  if (simulation.phase === 'completed') {
    return (
      <div className="flex flex-col h-full bg-app">
        <div className="px-4 py-3 border-b border-line flex-shrink-0">
          <button onClick={() => navigate(`/exam/${examId}`)} className="flex items-center gap-2 text-fg-subtle hover:text-fg transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Retour</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <ResultsSummary
            mode="simulation"
            abandoned={simulation.abandoned}
            results={simulation.results}
            questions={simulation.questions}
            examId={examId}
            examName={exam.name}
            totalTime={simulation.totalTimeElapsed}
            totalPoints={simulation.totalPoints}
            passingScore={exam.passingScore}
            onRestart={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  // --- Running ---
  if (simulation.phase === 'running' && simulation.currentQuestion) {
    const isFirst = simulation.currentIndex === 0;
    const isLast = simulation.currentIndex >= simulation.totalQuestions - 1;
    const unansweredCount = simulation.totalQuestions - simulation.answeredCount;
    const flaggedCount = Object.values(simulation.flaggedQuestions).filter(Boolean).length;
    const progressPercent = Math.round((simulation.answeredCount / simulation.totalQuestions) * 100);

    return (
      <>
      <div className="flex flex-col h-full bg-app">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-line">
          {/* Top row: quit + timer + submit */}
          <div className="px-4 sm:px-6 pt-3 pb-2 flex items-center justify-between">
            <button onClick={handleQuit} className="flex items-center gap-1.5 text-fg-subtle hover:text-red-400 transition-colors">
              <LogOut className="w-4 h-4" />
              <span className="text-xs font-medium hidden sm:inline">Quitter</span>
            </button>

            {/* BIG TIMER — center */}
            <SimulationTimer
              timeLimit={durationSeconds}
              isRunning={timerRunning}
              onTimeUp={handleTimeUp}
            />

            <button
              onClick={handleSubmit}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-orange-500/15 text-orange-400 border border-orange-500/20 hover:bg-orange-500/25 transition-colors"
            >
              <CheckCircle className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Terminer</span>
            </button>
          </div>

          {/* Progress bar */}
          <div className="px-4 sm:px-6 pb-2">
            <div className="flex items-center gap-3 mb-1.5">
              <span className="text-xs text-fg-subtle tabular-nums">
                Question {simulation.currentIndex + 1}/{simulation.totalQuestions}
              </span>
              <div className="flex-1 h-1 bg-overlay-2 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="text-xs text-fg-faint tabular-nums">{simulation.answeredCount}/{simulation.totalQuestions}</span>
            </div>

            <div className="flex items-center gap-3 text-[10px] sm:text-[11px] text-fg-faint">
              <span className="inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400" />Répondue
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />À revoir
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-overlay-3" />Non répondue
              </span>
              {flaggedCount > 0 && (
                <span className="ml-auto text-orange-400/70">
                  <Flag className="w-3 h-3 inline mr-0.5" />{flaggedCount}
                </span>
              )}
            </div>
          </div>

          {/* Question navigator */}
          <div ref={navScrollRef} className="flex gap-1 overflow-x-auto scrollbar-hide px-4 sm:px-6 pb-3">
            {simulation.questions.map((q, i) => {
              const answered = simulation.answers[q.id] !== undefined;
              const flagged = simulation.flaggedQuestions[q.id];
              const isCurrent = i === simulation.currentIndex;
              return (
                <button
                  key={q.id}
                  ref={(el) => setQuestionBtnRef(i, el)}
                  onClick={() => simulation.goToQuestion(i)}
                  className={`w-8 h-8 flex-shrink-0 rounded-lg text-[11px] font-bold transition-all duration-200 ${
                    isCurrent
                      ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30 scale-110'
                      : flagged
                        ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                        : answered
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-overlay-1 text-fg-faint hover:bg-overlay-3'
                  }`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
        </div>

        {/* Question content */}
        <div ref={contentScrollRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="max-w-2xl mx-auto space-y-4 sm:space-y-5">
            <div className="flex items-center justify-between">
              <button
                onClick={() => simulation.toggleFlagQuestion(simulation.currentQuestion!.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  simulation.flaggedQuestions[simulation.currentQuestion.id]
                    ? 'bg-orange-500/20 text-orange-300 border-orange-500/30'
                    : 'bg-overlay-1 text-fg-subtle border-line hover:bg-overlay-3'
                }`}
              >
                <Flag className="w-3 h-3" />
                {simulation.flaggedQuestions[simulation.currentQuestion.id] ? 'Marquée' : 'À revoir'}
              </button>
              {unansweredCount > 0 && (
                <button
                  onClick={simulation.goToNextUnanswered}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-fg-subtle bg-overlay-1 border border-line hover:bg-overlay-3"
                >
                  Suivante vide
                  <ChevronRight className="w-3 h-3" />
                </button>
              )}
            </div>
            <QuestionCard
              question={simulation.currentQuestion}
              questionNumber={simulation.currentIndex + 1}
              totalQuestions={simulation.totalQuestions}
            />
            <OptionsList
              options={simulation.currentQuestion.options}
              selectedIndex={simulation.selectedAnswer}
              answerState="unanswered"
              onSelect={handleAnswerSelect}
              disabled={false}
            />
          </div>
        </div>

        {/* Footer navigation */}
        <div className="flex-shrink-0 px-4 sm:px-6 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] border-t border-line bg-app/95 backdrop-blur-sm">
          <div className="max-w-2xl mx-auto flex items-center gap-2">
            <button
              onClick={() => simulation.goToQuestion(simulation.currentIndex - 1)}
              disabled={isFirst}
              className="flex items-center gap-1 px-4 py-2.5 bg-overlay-1 border border-line rounded-xl text-sm text-fg-subtle hover:bg-overlay-2 disabled:opacity-30 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Préc.</span>
            </button>

            {isLast ? (
              <button
                onClick={handleSubmit}
                className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold text-sm hover:from-orange-600 hover:to-amber-600 transition-all active:scale-[0.98] shadow-lg shadow-orange-500/20"
              >
                Terminer l'examen
              </button>
            ) : (
              <button
                onClick={() => simulation.goToQuestion(simulation.currentIndex + 1)}
                className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold text-sm hover:from-green-700 hover:to-emerald-700 transition-all active:scale-[0.98]"
              >
                Suivante
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Review modal */}
      {showReviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg rounded-2xl bg-surface border border-line p-5 sm:p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-500/15 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <h2 className="text-base font-bold text-fg">Vérifier avant soumission</h2>
                <p className="text-xs text-fg-subtle">{exam.name}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-green-500/5 border border-green-500/10 rounded-xl p-3 text-center">
                <p className="text-xs text-fg-subtle">Répondues</p>
                <p className="text-xl font-bold text-green-400">{simulation.answeredCount}</p>
              </div>
              <div className={`border rounded-xl p-3 text-center ${unansweredCount > 0 ? 'bg-red-500/5 border-red-500/10' : 'bg-overlay-1 border-line'}`}>
                <p className="text-xs text-fg-subtle">Non répondues</p>
                <p className={`text-xl font-bold ${unansweredCount > 0 ? 'text-red-400' : 'text-fg-faint'}`}>{unansweredCount}</p>
              </div>
            </div>

            {unansweredCount > 0 && (
              <div className="flex items-start gap-2 bg-amber-500/5 border border-amber-500/10 rounded-xl p-3 mb-4">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-300/70">
                  Attention : {unansweredCount} question{unansweredCount > 1 ? 's' : ''} sans réponse. Les questions non répondues seront comptées comme incorrectes.
                </p>
              </div>
            )}

            <div className="max-h-40 overflow-y-auto rounded-xl bg-overlay-1 p-3 mb-5">
              <div className="grid grid-cols-10 sm:grid-cols-12 gap-1">
                {simulation.questions.map((q, i) => {
                  const answered = simulation.answers[q.id] !== undefined;
                  const flagged = simulation.flaggedQuestions[q.id];
                  return (
                    <button
                      key={q.id}
                      onClick={() => { simulation.goToQuestion(i); setShowReviewModal(false); }}
                      className={`h-7 rounded-md text-[10px] font-bold transition-colors ${
                        flagged
                          ? 'bg-orange-500/20 text-orange-300'
                          : answered
                            ? 'bg-green-500/15 text-green-400'
                            : 'bg-red-500/10 text-red-400/60'
                      }`}
                    >
                      {i + 1}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowReviewModal(false)}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-fg-muted border border-line hover:bg-overlay-1 transition-colors"
              >
                Continuer
              </button>
              <button
                onClick={() => { setShowReviewModal(false); simulation.submitExam(); }}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 transition-all"
              >
                Terminer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quit confirmation modal */}
      {showQuitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm rounded-2xl bg-surface border border-line p-5 sm:p-6 shadow-2xl text-center">
            <div className="w-12 h-12 bg-red-500/15 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-6 h-6 text-red-400" />
            </div>
            <h2 className="text-base font-bold text-fg mb-2">Quitter la simulation ?</h2>
            <p className="text-sm text-fg-subtle mb-1">
              Vous avez répondu à <span className="text-fg font-semibold">{simulation.answeredCount}</span> question{simulation.answeredCount > 1 ? 's' : ''} sur <span className="text-fg font-semibold">{simulation.totalQuestions}</span>.
            </p>
            <p className="text-xs text-fg-faint mb-5">
              Vos réponses seront soumises et vous verrez vos résultats.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowQuitModal(false)}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-fg-muted border border-line hover:bg-overlay-1 transition-colors"
              >
                Continuer
              </button>
              <button
                onClick={handleConfirmQuit}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Quitter et voir résultats
              </button>
            </div>
          </div>
        </div>
      )}
      </>
    );
  }

  // --- Intro phase ---
  return (
    <div className="flex flex-col h-full bg-app pb-20 lg:pb-0">
      <div className="px-4 sm:px-6 py-3 border-b border-line flex-shrink-0">
        <button onClick={() => navigate(`/exam/${examId}`)} className="flex items-center gap-2 text-fg-subtle hover:text-fg transition-colors group">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          <span className="text-sm font-medium">Retour</span>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="flex items-center justify-center min-h-full px-4 sm:px-6 py-8 sm:py-12">
          <div className="w-full max-w-md">
            {/* Hero */}
            <div className="text-center mb-6 sm:mb-8">
              <div className={`w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br ${gradient} rounded-3xl flex items-center justify-center text-4xl sm:text-5xl mx-auto mb-5 shadow-2xl shadow-black/30`}>
                {exam.icon}
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-500/10 border border-orange-500/20 rounded-full mb-3">
                <Zap className="w-3 h-3 text-orange-400" />
                <span className="text-[10px] sm:text-xs font-semibold text-orange-400 uppercase tracking-wider">Mode Simulation</span>
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-fg mb-1.5">{exam.name}</h1>
              <p className="text-sm text-fg-subtle">Conditions d'examen réelles</p>
            </div>

            {simulation.totalQuestions === 0 ? (
              <div className="bg-red-500/5 border border-red-500/15 rounded-2xl p-5 mb-5 text-center">
                <BookOpen className="w-10 h-10 text-red-400/40 mx-auto mb-3" />
                <p className="text-sm font-medium text-fg-subtle">Aucune question disponible</p>
                <p className="text-xs text-fg-faint mt-1">Ajoutez des questions depuis l'administration.</p>
              </div>
            ) : (
              <>
                {/* Choix du format (Éclair / Moyen / Complet) — l'éclair réduit
                    l'abandon des longues simulations. */}
                {lengthOptions.length > 1 && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-fg-subtle uppercase tracking-wider mb-2">Format</p>
                    <div className="grid grid-cols-3 gap-2">
                      {lengthOptions.map((o) => {
                        const active = effectiveQuestionCount === o.value;
                        return (
                          <button
                            key={o.key}
                            onClick={() => setChosenCount(o.value)}
                            className={`rounded-2xl p-3 text-center border transition-all active:scale-[0.98] ${
                              active
                                ? 'bg-orange-500/15 border-orange-500/40 text-fg'
                                : 'bg-surface border-line text-fg-muted hover:border-orange-500/30'
                            }`}
                          >
                            <p className="text-sm font-bold">{o.label}</p>
                            <p className="text-lg font-extrabold leading-tight">{o.value}</p>
                            <p className="text-[10px] text-fg-faint">{o.hint}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Résumé compact du format choisi (le nb de questions est déjà
                    porté par les cartes de format ci-dessus). */}
                <div className="flex items-center justify-center gap-4 text-sm text-fg-subtle mb-4">
                  <span className="inline-flex items-center gap-1.5">
                    <Timer className="w-4 h-4 text-orange-400" />
                    <strong className="text-fg">{effectiveDurationMinutes}</strong> min
                  </span>
                  <span className="w-1 h-1 rounded-full bg-overlay-3" />
                  <span className="inline-flex items-center gap-1.5">
                    <Shield className="w-4 h-4 text-green-400" />
                    Seuil <strong className="text-fg">{exam.passingScore}%</strong>
                  </span>
                </div>

                {/* Rules */}
                <div className="bg-overlay-1 border border-line rounded-2xl p-4 mb-5 space-y-2.5">
                  <RuleItem icon={<Timer className="w-3.5 h-3.5 text-orange-400" />} text="Compte à rebours — l'examen se termine automatiquement" />
                  <RuleItem icon={<Shuffle className="w-3.5 h-3.5 text-blue-400" />} text="Questions mélangées de toutes les catégories" />
                  <RuleItem icon={<ChevronLeft className="w-3.5 h-3.5 text-emerald-400" />} text="Navigation libre entre les questions" />
                  <RuleItem icon={<BarChart3 className="w-3.5 h-3.5 text-purple-400" />} text="Résultats et corrections à la fin uniquement" />
                </div>

                {/* Start button */}
                <button
                  onClick={handleStart}
                  className="w-full py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-2xl font-bold text-base flex items-center justify-center gap-2.5 hover:from-orange-600 hover:to-amber-600 transition-all active:scale-[0.98] shadow-xl shadow-orange-500/20"
                >
                  <Play className="w-5 h-5" />
                  Commencer la simulation
                </button>
              </>
            )}

            {/* Categories */}
            {exam.categories.length > 0 && (
              <div className="mt-6 sm:mt-8">
                <p className="text-xs font-semibold text-fg-subtle uppercase tracking-wider mb-2.5">Catégories couvertes</p>
                <div className="flex flex-wrap gap-1.5">
                  {exam.categories.map((cat) => (
                    <span key={cat.id} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 bg-overlay-1 text-fg-subtle rounded-lg border border-line">
                      <span>{cat.icon || '📖'}</span>
                      {cat.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Big visible timer component ---
function SimulationTimer({ timeLimit, isRunning, onTimeUp }: { timeLimit: number; isRunning: boolean; onTimeUp: () => void }) {
  const { timeRemaining, percentRemaining } = useTimer({
    initialTime: timeLimit,
    onTimeUp,
    autoStart: isRunning,
  });
  const [muted, setMuted] = useState(isSoundMuted());
  const prevTimeRef = useRef(timeRemaining);

  const isUrgent = percentRemaining <= 10;
  const isLow = percentRemaining <= 25;
  const isHalf = percentRemaining <= 50;

  // Play tick sounds
  useEffect(() => {
    if (!isRunning || timeRemaining === prevTimeRef.current) {
      prevTimeRef.current = timeRemaining;
      return;
    }
    prevTimeRef.current = timeRemaining;

    if (isUrgent) {
      playUrgentTick();
    } else if (isLow && timeRemaining % 2 === 0) {
      playTick();
    } else if (isHalf && timeRemaining % 5 === 0) {
      playTick();
    }
  }, [timeRemaining, isRunning, isUrgent, isLow, isHalf]);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setSoundMuted(next);
  };

  const colorClass = isUrgent
    ? 'text-red-400'
    : isLow
      ? 'text-amber-400'
      : isHalf
        ? 'text-yellow-300'
        : 'text-green-400';

  const bgClass = isUrgent
    ? 'bg-red-500/10 border-red-500/20'
    : isLow
      ? 'bg-amber-500/10 border-amber-500/15'
      : isHalf
        ? 'bg-yellow-500/5 border-yellow-500/10'
        : 'bg-overlay-1 border-line';

  const mins = Math.floor(timeRemaining / 60);
  const secs = timeRemaining % 60;

  return (
    <div className="flex items-center gap-1.5">
      <div className={`flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl border transition-colors duration-500 ${bgClass} ${isUrgent ? 'animate-timer-blink' : ''}`}>
        <Clock className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors duration-500 ${colorClass}`} />
        <span className={`text-lg sm:text-2xl font-mono font-bold tabular-nums tracking-tight transition-colors duration-500 ${colorClass}`}>
          {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
        </span>
      </div>
      <button
        onClick={toggleMute}
        className="p-1.5 rounded-lg text-fg-faint hover:text-fg-subtle hover:bg-overlay-1 transition-colors"
        title={muted ? 'Activer le son' : 'Couper le son'}
      >
        {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function RuleItem({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="mt-0.5 flex-shrink-0">{icon}</div>
      <p className="text-xs text-fg-subtle leading-relaxed">{text}</p>
    </div>
  );
}
